import SearchIcon from "@mui/icons-material/Search";
import {
	Button,
	Grid2,
	IconButton,
	InputAdornment,
	Paper,
	TextField,
	Typography,
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import {
	ColDef,
	DefaultMenuItem,
	GetContextMenuItemsParams,
	GridApi,
	MenuItemDef,
} from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import * as React from "react";
import { useEffect, useMemo, useState } from "react";
import { ErrorModal } from "../../../../../components/modals/error-modal";
import { CenteredLazyLoading } from "../../../../../components/shared/CenteredLazyLoading";
import { useContextMenuMESF } from "../../../../../controls/contextMenu";
import { themeDXT } from "../../../../../css/themeMESF";
import { User } from "../../models/User";
import { getUsers } from "../../repositories/UsersRepository";
import { ChangePassword } from "../ChangePassword/ChangePassword";
import { CreateUser } from "../Create/CreateUser";
import { DeleteUser } from "../Delete/DeleteUser";
import { EditUser } from "../EditUser/EditUser";
import { useTableData } from "./customHooks/useTableData";
import { useUsersOptionsFunctions } from "./customHooks/useUsersOptionsFunctions";

const useSearchUsers = () => {
	return useQuery<User[], Error>({
		queryKey: ["users"],
		queryFn: ({ signal }) => getUsers(signal),
	});
};

export const TableUsers = () => {
	const [openModalCreate, setOpenModalCreate] = useState(false);
	const [openModalEditDataUser, setOpenModalEditDataUser] = useState(false);
	const [openModalDeleteUser, setOpenModalDeleteUser] = useState(false);
	const [openModalChangePassword, setOpenModalChangePasswordd] =
		useState(false);

	const [error, setError] = useState("");
	const [searchText, setSearchText] = useState("");

	const [userId, setUserId] = useState<number | null>(null);
	const [gridApi, setGridApi] = useState<GridApi | null>(null);

	const {
		data: rows,
		isLoading,
		isError,
		error: e,
		refetch,
	} = useSearchUsers();

	const formattedRows = rows?.map(
		({ UserId, UserName, LastName, FirstName, Profiles }) => ({
			UserId,
			UserName,
			LastName,
			FirstName,
			Profiles: (Profiles as string)?.replaceAll("Profile", ""),
			id: UserId,
		}),
	);

	const { getMenuOptions } = useUsersOptionsFunctions({
		setUserId,
		setShowEditModal: setOpenModalEditDataUser,
		setShowDeleteModal: setOpenModalDeleteUser,
		setShowCreateModal: setOpenModalCreate,
		setShowChangePasswordModal: setOpenModalChangePasswordd,
	});

	const { showContextMenu, registerConfig } = useContextMenuMESF();

	const { columnDefs } = useTableData({
		setUserId,
		setOpenModalEditDataUser,
		setOpenModalDeleteUser,
		setOpenModalChangePasswordd,
		showContextMenu,
	});

	const defaultColDef = useMemo<ColDef>(() => {
		return {
			sortable: true,
			wrapText: true,
			autoHeight: true,
			wrapHeaderText: true,
			suppressHeaderMenuButton: true,
			cellStyle: {
				display: "flex",
				alignItems: "center",
				justifyContent: "center",
			},
		};
	}, []);

	const getContextMenuItems = (
		params: GetContextMenuItemsParams,
	): (DefaultMenuItem | MenuItemDef)[] => {
		const data = params.node?.data;
		params.api.deselectAll();
		params.node?.setSelected(true);
		showContextMenu(event as any, data, "TableUsers");
		return [];
	};

	useEffect(() => {
		registerConfig({
			id: "TableUsers",
			getOptions: getMenuOptions,
		});
	}, []);

	useEffect(() => {
		if (e && isError) {
			setError(e.message);
		}
	}, [e, isError]);

	return (
		<>
			<Grid2 container justifyContent="flex-start" p={1} spacing={1}>
				<Grid2 size={{ md: 12, xs: 12 }}>
					<Typography variant="h5" fontWeight={600}>
						User Management
					</Typography>
				</Grid2>
				<Grid2 size={{ md: 12, xs: 12 }}>
					<TextField
						fullWidth
						label="Search"
						value={searchText}
						onChange={(event: any) => {
							setSearchText(event.target.value);
							gridApi?.setGridOption("quickFilterText", event.target.value);
						}}
						slotProps={{
							input: {
								startAdornment: (
									<InputAdornment position="start">
										<IconButton edge="start" type="submit">
											<SearchIcon />
										</IconButton>
									</InputAdornment>
								),
							} as any,
						}}
					/>
				</Grid2>
				<Grid2 container justifyContent="center" alignItems="center">
					<Grid2 size={{ md: 12, xs: 12 }}>
						<Paper
							style={{
								height: "70vh",
							}}
						>
							<AgGridReact
								loading={isLoading}
								gridOptions={{
									theme: themeDXT,
								}}
								rowData={formattedRows || []}
								columnDefs={columnDefs}
								defaultColDef={defaultColDef}
								rowHeight={34}
								headerHeight={34}
								loadingOverlayComponent={CenteredLazyLoading}
								animateRows={true}
								getContextMenuItems={getContextMenuItems}
								getRowId={(params) => params.data.UserId}
								rowSelection="single"
								onRowDoubleClicked={(event) => {
									setUserId(event.data.UserId);
									setOpenModalEditDataUser(true);
								}}
								onGridReady={(params) => setGridApi(params.api)}
							/>
						</Paper>
					</Grid2>
					<Grid2 container justifyContent="flex-end">
						<Grid2 size={{ md: 2, xs: 12 }}>
							<Button
								variant="contained"
								color="primary"
								onClick={() => setOpenModalCreate(!openModalCreate)}
								fullWidth
							>
								New user
							</Button>
						</Grid2>
					</Grid2>
				</Grid2>
			</Grid2>
			<CreateUser
				show={openModalCreate}
				onHide={(shouldUpdate: boolean) => {
					setOpenModalCreate(false);
					setUserId(null);
					if (shouldUpdate) refetch();
				}}
				suffixTitle="User Management"
			/>
			<EditUser
				show={openModalEditDataUser}
				userId={userId}
				onHide={(shouldUpdate: boolean) => {
					setOpenModalEditDataUser(false);
					setUserId(null);
					if (shouldUpdate) refetch();
				}}
				suffixTitle="User Management"
			/>
			<ChangePassword
				show={openModalChangePassword}
				userId={userId}
				onHide={(shouldUpdate) => {
					setOpenModalChangePasswordd(false);
					setUserId(null);
					if (shouldUpdate) {
						refetch();
					}
				}}
				suffixTitle="User Management"
			/>
			<DeleteUser
				show={openModalDeleteUser}
				userId={userId}
				onHide={(shouldUpdate: boolean) => {
					setOpenModalDeleteUser(false);
					setUserId(null);
					if (shouldUpdate) refetch();
				}}
				suffixTitle="User Management"
			/>
			<ErrorModal error={error} onHide={() => setError("")} />
		</>
	);
};
